import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import AddItemDialog from "./AddItemDialog";
import { formFieldStyles, labelStyles } from "../../styles/formStyles";
import { ICreateOrderDTO, IInventoryQuantityResponseDTO, IOrderListDTO, IOrderResponseDTO } from "../../interfaces/Orders";
import dayjs, { Dayjs } from "dayjs";
import { FormErrors } from "../../interfaces/Errors";
import { addOrder, getOrder, updateOrder, getWarehousePincodes, getWarehousePincodesPublic, getWarehouseByCompanyId } from "../../services/Orders";
import { useAuth } from "../../auth/AuthProvider";
import { IPincodeResponseDTO } from "../../interfaces/Orders";

interface AddedItem extends IInventoryQuantityResponseDTO {
  quantity: number;
}

interface AddOrderPageProps {
  order: IOrderListDTO | null;
  onSave: () => void;
  onCancel: () => void;
  publicCompanyId?: number;
  publicOrderId?: number;
  exCompanyId?: number;
  hideBackButton?: boolean;
}

const AddOrderPage = ({ order, onSave, onCancel, publicCompanyId, publicOrderId, exCompanyId, hideBackButton }: AddOrderPageProps) => {
  const { getLoginUserInfo } = useAuth();
  const loggedInUser = getLoginUserInfo();
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const landmarkInputRef = useRef<HTMLInputElement | null>(null);
  const [addedItems, setAddedItems] = useState<AddedItem[]>([]);
  const [customerDetails, setCustomerDetails] = useState<ICreateOrderDTO>({} as ICreateOrderDTO);
  const [deliveryDateValue, setDeliveryDateValue] = useState<Dayjs | null>(null);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [msg, setMsg] = useState("");
  const [openError, setOpenError] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pincodes, setPincodes] = useState<IPincodeResponseDTO[]>([]);
  const [warehouseId, setWarehouseId] = useState<number | undefined>(loggedInUser?.workspaceId);
  const companyId = publicCompanyId ?? loggedInUser?.companyId;

  useEffect(() => {
    (async () => {
      try {
        // If user doesn't have workspace ID, fetch warehouse by company ID
        if (!warehouseId && companyId) {
          const warehouseRes = await getWarehouseByCompanyId(companyId);
         if (warehouseRes && warehouseRes.length > 0) {
              setWarehouseId(warehouseRes[0]);
            }

        }
        
        const res = (publicOrderId && companyId ) ? await getWarehousePincodesPublic(companyId) : await getWarehousePincodes();
        if (res) setPincodes(res as IPincodeResponseDTO[]);
      } catch { /* ignore */ }
    })();
  }, [warehouseId, companyId, publicOrderId]);

  useEffect(() => {
    if (!order?.orderId || pincodes.length === 0) return;
    
    (async () => {
      try {
        setLoading(true);
        const res: IOrderResponseDTO = await getOrder(order.orderId);
        if (res) {
          const details: any = {
            name: res.name || '',
            phone: res.phone || '',
            email: res.email || '',
            deliveryDate: res.deliveryDate || '',
            pincode: res.pincode || '',
            address: res.address || '',
            landmark: '',
            latitude: res.customerLat || 0,
            longitude: res.customerLng || 0,
          };

          if (res.customerLat && res.customerLng) {
            try {
              const geocoder = new google.maps.Geocoder();
              const result = await geocoder.geocode({ location: { lat: res.customerLat, lng: res.customerLng } });
              if (result.results?.[0]) {
                details.landmark = result.results[0].formatted_address;
              }
            } catch { /* ignore */ }
          }

          setCustomerDetails(details as unknown as ICreateOrderDTO);
          
          if (res.deliveryDate) {
            setDeliveryDateValue(dayjs(res.deliveryDate));
          }
          
          if (res.items?.length) {
            setAddedItems(
              res.items.map((item) => ({
                itemId: item.inventoryId,
                itemName: item.itemName || '',
                inventoryIds: [item.inventoryId],
                typeId: 0,
                typeName: item.typeName || '',
                availableQuantity: 0,
                imageUrl: item.imageUrl || '',
                price: item.unitCost,
                quantity: item.quantity,
              }))
            );
          }
        }
      } catch {
        setOpenError(true);
        setMsg("Error fetching order details.");
      } finally {
        setLoading(false);
      }
    })();
  }, [order?.orderId, pincodes]);

  useEffect(() => {
    if (!landmarkInputRef.current || !window.google?.maps?.places) return;
    const ac = new google.maps.places.Autocomplete(landmarkInputRef.current, { types: ["geocode", "establishment"] });
    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (place?.geometry?.location) {
        setCustomerDetails((prev: any) => ({
          ...prev,
          landmark: place.formatted_address || place.name || '',
          latitude: place.geometry!.location!.lat(),
          longitude: place.geometry!.location!.lng(),
        }));
        setErrors((prev) => ({ ...prev, landmark: "" }));
      }
    });
    autocompleteRef.current = ac;
  }, [landmarkInputRef.current]);

  const handleAddItem = (newItem: IInventoryQuantityResponseDTO) => {
    setAddedItems([...addedItems, { ...newItem, quantity: 1 }]);
    setAddItemOpen(false);
  };

  const handleAddedItemQuantityChange = (index: number, delta: number) => {
    setAddedItems(
      addedItems.map((item, i) =>
        i === index
          ? { ...item, quantity: Math.min(Math.max(1, item.quantity + delta), item.availableQuantity) }
          : item
      )
    );
  };

  const handleDeleteAddedItem = (index: number) => {
    setAddedItems(addedItems.filter((_, i) => i !== index));
  };

  const calculateSummary = () => {
    const itemCost = addedItems.reduce(
      (sum: number, item: AddedItem) => sum + item.quantity * item.price,
      0
    );
    const shipping = 0;
    const offers = 0;
    const totalCost = itemCost + shipping - offers;
    return { itemCost, shipping, offers, totalCost };
  };

  const handleDeliveryDate = (date: Dayjs | null) => {
    setCustomerDetails({
      ...customerDetails,
      deliveryDate: date ? dayjs(date).format("YYYY-MM-DD") : "",
    });
  };
  const summary = calculateSummary();

  const validate = () => {
    const newErrors: typeof errors = {};

    if (!customerDetails.name?.trim()) {
      newErrors.name = "Name is required";
    }

    if (!customerDetails.phone?.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!/^\d{10}$/.test(customerDetails.phone)) {
      newErrors.phone = "Phone number must be 10 digits";
    }

    if (!customerDetails.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(customerDetails.email)) {
      newErrors.email = "Enter a valid email";
    }

    if (!customerDetails.deliveryDate) {
      newErrors.deliveryDate = "Delivery Date is required";
    }

    if (!customerDetails.pincode?.trim()) {
      newErrors.pincode = "Pincode is required";
    }

    if (!customerDetails.address?.trim()) {
      newErrors.address = "Address is required";
    }

    if (!(customerDetails as any).landmark?.trim()) {
      newErrors.landmark = "Landmark is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = async () => {
    if (!validate()) {
      return;
    }
    if (!companyId || !warehouseId) {
      setOpenError(true);
      setMsg("Company or Warehouse information is missing.");
      return;
    }
    setLoading(true);
    try {
      const post = {} as ICreateOrderDTO;
      post.companyId = companyId;
      post.warehouseId = warehouseId;
      post.exCompanyId = exCompanyId;
      post.exOrderId=publicOrderId;
      post.items = addedItems.flatMap((item) =>
        item.inventoryIds.slice(0, item.quantity).map((id) => ({
          inventoryId: id,
          quantity: 1,
        }))
      );
      post.name = customerDetails.name;
      post.phone = customerDetails.phone;
      post.email = customerDetails.email;
      post.deliveryDate = customerDetails.deliveryDate.includes("T") ? customerDetails.deliveryDate : customerDetails.deliveryDate + "T00:00:00";
      post.pincode = customerDetails.pincode;
      post.address = customerDetails.address;
      post.latitude = customerDetails.latitude || 0;
      post.longitude = customerDetails.longitude || 0;
      post.itemsCost = summary.itemCost;
      
      const response = order?.orderId 
        ? await updateOrder(post, order.orderId)
        : await addOrder(post);
        
      if(response && response.status === "success"){
        setOpenSuccess(true);
        setMsg(order?.orderId ? "Order updated successfully." : "Order created successfully.");
        onSave();
      }else{
        setOpenError(true);
        setMsg(order?.orderId ? "Error updating order." : "Error creating order.");
      }
    }catch(error) {
      setOpenError(true);
      setMsg("Error saving order.");
    }finally{
      setLoading(false);
    }
  };
  const handleSnackbarClose = () => {
    setOpenError(false);
    if(publicOrderId){
      setOpenSuccess(false);
      // Reset form fields after success
      setCustomerDetails({} as ICreateOrderDTO);
      setAddedItems([]);
      setDeliveryDateValue(null);
    }else{
      setOpenSuccess(false);
    }
  };
  return (
    <>
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", backgroundColor: "#F5F5F5" }}>
      {!hideBackButton && (
        <Box sx={{ p: 1 }}>
          <Button startIcon={<ChevronLeftIcon />} onClick={onCancel} sx={{ textTransform: "none", color: "text.primary", p: 0, minWidth: "auto", fontSize: "14px" }}>
            Back
          </Button>
        </Box>
      )}
      
      <Paper sx={{ flex: 1, m: 1, mt: 0, display: "flex", flexDirection: "column", overflow: "hidden", borderRadius: "8px" }}>
        <Box sx={{ px: 2}}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1, flexShrink: 0 }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {order ? "Edit Order" : "Add Order"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>
                Add Order details here. Click add when you're done.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
          <Box sx={{p:1, display: "flex", gap:2}}>
          {/* Left Side - Added Items */}
          <Box sx={{ width: "70%", display: "flex", flexDirection: "column", minWidth: 0, justifyContent: "space-between" }}>
            <Box sx={{ flex: 1, bgcolor: "background.default", p: 2, borderRadius: "8px" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                <Typography variant="subtitle1" fontWeight={600}>Items</Typography>
                <Button variant="contained" endIcon={<AddIcon />} onClick={() => setAddItemOpen(true)} sx={{ borderRadius: "8px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset", padding: "4px 8px", textTransform: "none", fontSize: "13px", fontWeight: 500, lineHeight: "20px", whiteSpace: "nowrap", "&:hover": { backgroundColor: "primary.dark", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset" } }}>
                  Add Item
                </Button>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ width: "50%" }}>Item</Typography>
                <Typography variant="subtitle2" fontWeight={600} sx={{ width: "25%", textAlign: "center" }}>Quantity</Typography>
                <Typography variant="subtitle2" fontWeight={600} sx={{ width: "25%", textAlign: "center" }}>Total cost</Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {addedItems.map((item, index) => (
                  <Paper key={index} sx={{ p: "8px 12px", display: "flex", alignItems: "center", bgcolor: "background.paper", borderRadius: "14px", border: "1px solid rgba(74, 85, 101, 0.10)" }}>
                    <Box sx={{ width: "50%", display: "flex", alignItems: "center", gap: 1 }}>
                      <Box component="img" src={item.imageUrl || "/placeholder.png"} sx={{ width: 35, height: 35, borderRadius: "0.5rem", objectFit: "contain" }} />
                      <Box>
                        <Typography fontWeight={600}>{item.itemName}</Typography>
                        <Typography variant="body2" color="grey.primary">{item.typeName}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ width: "25%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, borderRadius: "18px", border: "1px solid rgba(74, 85, 101, 0.10)", bgcolor: "#F9FAFB", p: "2px" }}>
                        <IconButton size="small" onClick={() => handleAddedItemQuantityChange(index, -1)}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ minWidth: "1.5rem", textAlign: "center" }}>{item.quantity}</Typography>
                        <IconButton size="small" onClick={() => handleAddedItemQuantityChange(index, 1)}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {item.quantity >= item.availableQuantity && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, fontSize: "10px" }}>Max: {item.availableQuantity}</Typography>
                      )}
                    </Box>
                    <Box sx={{ width: "25%", display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
                      <Typography fontWeight={600}>₹ {item.price * item.quantity}</Typography>
                      <IconButton onClick={() => handleDeleteAddedItem(index)}>
                        <Box component="img" src="/delete.svg" alt="Delete" sx={{ width: "20px", height: "20px" }} />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Right Side - Customer Details */}
          <Box sx={{ width: "30%", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <Box sx={{ flex: 1, bgcolor: "background.default", p: 2, borderRadius: "8px" }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb:1 }}>Customer Details</Typography>
              
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box>
                  <Typography component="label" sx={labelStyles}>Name <span style={{ color: "red" }}>*</span></Typography>
                  <TextField 
                    fullWidth 
                    size="small" 
                    value={customerDetails.name || ''} 
                    onChange={(e) => {
                      setCustomerDetails({ ...customerDetails, name: e.target.value });
                      setErrors({ ...errors, name: "" });
                    }} 
                    error={!!errors.name}
                    helperText={errors.name}
                    sx={formFieldStyles} 
                  />
                </Box>

                <Box>
                  <Typography component="label" sx={labelStyles}>Phone Number <span style={{ color: "red" }}>*</span></Typography>
                  <TextField 
                    fullWidth 
                    size="small" 
                    value={customerDetails.phone || ''} 
                    onChange={(e) => {
                      setCustomerDetails({ ...customerDetails, phone: e.target.value });
                      setErrors({ ...errors, phone: "" });
                    }} 
                    error={!!errors.phone}
                    helperText={errors.phone}
                    sx={formFieldStyles} 
                  />
                </Box>

                <Box>
                  <Typography component="label" sx={labelStyles}>Email <span style={{ color: "red" }}>*</span></Typography>
                  <TextField 
                    fullWidth 
                    size="small" 
                    type="email"
                    value={customerDetails.email || ''} 
                    onChange={(e) => {
                      setCustomerDetails({ ...customerDetails, email: e.target.value });
                      setErrors({ ...errors, email: "" });
                    }} 
                    error={!!errors.email}
                    helperText={errors.email}
                    sx={formFieldStyles} 
                  />
                </Box>

                <Box>
                  <Typography component="label" sx={labelStyles}>Delivery Date <span style={{ color: "red" }}>*</span></Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={deliveryDateValue}
                      onChange={(date: Dayjs | null) => {
                        setDeliveryDateValue(date);
                        handleDeliveryDate(date);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          error: !!errors.deliveryDate,
                          helperText: errors.deliveryDate,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>

                <Box>
                  <Typography component="label" sx={labelStyles}>Pincode <span style={{ color: "red" }}>*</span></Typography>
                  <TextField 
                    select
                    fullWidth 
                    size="small" 
                    value={customerDetails.pincode || ''} 
                    onChange={(e) => {
                      setCustomerDetails({ ...customerDetails, pincode: e.target.value });
                      setErrors({ ...errors, pincode: "" });
                    }} 
                    error={!!errors.pincode}
                    helperText={errors.pincode}
                    sx={formFieldStyles}
                    SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 150 } } } }}
                  >
                    <MenuItem value="" disabled>Select Pincode</MenuItem>
                    {pincodes.map((p) => (
                      <MenuItem key={p.id} value={p.pincode}>{p.pincode}</MenuItem>
                    ))}
                  </TextField>
                </Box>

                <Box>
                  <Typography component="label" sx={labelStyles}>Address <span style={{ color: "red" }}>*</span></Typography>
                  <TextField 
                    fullWidth 
                    multiline 
                    rows={2} 
                    size="small" 
                    value={customerDetails.address || ''} 
                    onChange={(e) => {
                      setCustomerDetails({ ...customerDetails, address: e.target.value });
                      setErrors({ ...errors, address: "" });
                    }} 
                    error={!!errors.address}
                    helperText={errors.address}
                    sx={formFieldStyles} 
                  />
                </Box>

                <Box>
                  <Typography component="label" sx={labelStyles}>Landmark <span style={{ color: "red" }}>*</span></Typography>
                  <TextField
                    fullWidth
                    size="small"
                    inputRef={landmarkInputRef}
                    value={(customerDetails as any).landmark || ''}
                    onChange={(e) => {
                      setCustomerDetails({ ...customerDetails, landmark: e.target.value } as any);
                      setErrors({ ...errors, landmark: "" });
                    }}
                    error={!!errors.landmark}
                    helperText={errors.landmark}
                    sx={formFieldStyles}
                  />
                </Box>

                <Paper sx={{ p: 2, bgcolor: "background.default" }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Order Summary</Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="body2">Item Cost</Typography>
                    <Typography variant="body2">₹ {summary.itemCost}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="body2">Shipping</Typography>
                    <Typography variant="body2">₹ {summary.shipping}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2">Others</Typography>
                    <Typography variant="body2">₹ {summary.offers}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body1" fontWeight={600}>Total cost</Typography>
                    <Typography variant="body1" fontWeight={600}>₹ {summary.totalCost}</Typography>
                  </Box>
                </Paper>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", p: 2, backgroundColor: "#fff", borderTop: "1px solid", borderColor: "grey.100" }}>
            <Button variant="outlined" onClick={onCancel} sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset", padding: "8px 16px", textTransform: "none", "&:hover": { border: "1px solid", borderColor: "primary.main" } }}>
            Cancel
           </Button>
          <Button variant="contained"  disabled={loading} onClick={handleSave} sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset", padding: "8px 40px", textTransform: "none", "&:hover": { backgroundColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset" } }}>
          {loading ? <CircularProgress size={24} color="inherit" /> : order ? "Update Order" : "Add Order"}
          </Button>
        </Box>
      </Paper>

      <AddItemDialog open={addItemOpen} onClose={() => setAddItemOpen(false)} onAdd={handleAddItem} companyId={companyId} publicOrderId={!!publicOrderId} />
    </Box>
     <Snackbar
     anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
     open={openError}
     autoHideDuration={5000}
     onClose={handleSnackbarClose}
   >
     <Alert severity="error" variant="filled" sx={{ width: "100%",backgroundColor: "#FE0909",
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" } }}>
       {msg}
     </Alert>
   </Snackbar>

   <Snackbar
     anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
     open={openSuccess}
     autoHideDuration={5000}
     onClose={handleSnackbarClose}
   >
     <Alert severity="success" variant="filled" sx={{ width: "100%",backgroundColor: "#00B221",
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" } }}>
       {msg}
     </Alert>
   </Snackbar>
   </>
  );
};
export default AddOrderPage;
